import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Eye, CheckCircle, XCircle, Download, Image as ImageIcon, Video, Copy, Search, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { logUpdate } from '@/lib/audit-log';
import { DataPagination } from '@/components/DataPagination';

type SortConfig = {
  key: string;
  direction: 'asc' | 'desc';
} | null;

// Column configuration with display labels and database field mapping
const COLUMN_CONFIG = [
  { label: 'Title', field: 'title', sortable: true },
  { label: 'Brand ID', field: 'brand_id', sortable: false },
  { label: 'Status', field: 'status', sortable: false },
  { label: 'Total Brand Budget', field: 'budget_allocation_percent', sortable: true },
  { label: 'Actions', field: 'actions', sortable: false },
];

export default function BrandCampaigns() {
  const queryClient = useQueryClient();
  const [viewing, setViewing] = useState<any | null>(null);
  
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortConfig, setSortConfig] = useState<SortConfig>(null);
  const [isExporting, setIsExporting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['brand-campaigns', currentPage, pageSize, search, sortConfig?.key, sortConfig?.direction],
    queryFn: async () => {
      let query = supabase
        .from('campaigns')
        .select('*', { count: 'exact' });

      if (search) {
        query = query.ilike('title', `%${search}%`);
      }

      if (sortConfig) {
        query = query.order(sortConfig.key, {
          ascending: sortConfig.direction === 'asc',
        });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      query = query.range(
        (currentPage - 1) * pageSize,
        currentPage * pageSize - 1
      );

      const { data, count, error } = await query;

      if (error) throw error;

      return {
        data: data ?? [],
        total: count ?? 0,
      };
    },
  });

  // Fetch briefs for the currently viewed campaign
  const { data: briefs } = useQuery({
    queryKey: ['brand-campaign-briefs', viewing?.id],
    queryFn: async () => {
      if (!viewing?.id) return [];
      const { data } = await supabase
        .from('campaign_briefs')
        .select('*')
        .eq('campaign_id', viewing.id)
        .order('created_at', { ascending: true });
      return data ?? [];
    },
    enabled: !!viewing?.id,
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      // Fetch old data for audit log
      const { data: oldData } = await supabase.from('campaigns').select('*').eq('id', id).single();
      
      const { error } = await supabase
        .from('campaigns')
        .update({ status })
        .eq('id', id);
      if (error) throw error;

      // Audit log
      if (oldData) {
        await logUpdate('campaigns', id, oldData, { ...oldData, status }, {
          description: `Changed brand campaign status from "${oldData?.status}" to "${status}"`,
          metadata: {
            brand_name: oldData?.brand_name,
            product_name: oldData?.product_name,
            old_status: oldData?.status,
            new_status: status
          }
        });
      }
    },
    onSuccess: () => {
      toast.success('Campaign status updated');
      setViewing(null);
      queryClient.invalidateQueries({ queryKey: ['brand-campaigns'] });
    },
    onError: (err: any) => toast.error(err.message),
  });

  const updateCampaignMutation = useMutation({
    mutationFn: async (campaign: any) => {
      const id = campaign.id;
      const payload = { ...campaign };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      
      // Fetch old data for audit log
      const { data: oldData } = await supabase.from('campaigns').select('*').eq('id', id).single();
      
      const { error } = await supabase
        .from('campaigns')
        .update(payload)
        .eq('id', id);
      if (error) throw error;

      // Audit log
      if (oldData) {
        await logUpdate('campaigns', id, oldData, { ...payload, id }, {
          description: `Updated brand campaign "${payload.product_name || oldData.product_name}"`,
          metadata: {
            brand_name: payload.brand_name || oldData.brand_name,
            product_name: payload.product_name || oldData.product_name
          }
        });
      }
    },
    onSuccess: () => {
      toast.success('Campaign updated');
      setViewing(null);
      queryClient.invalidateQueries({ queryKey: ['brand-campaigns'] });
    },
    onError: (err: any) => toast.error(err.message),
  });

  const statusColor = (s: string) => {
    switch (s) {
      case 'active': return 'default';
      case 'pending': return 'secondary';
      case 'rejected': return 'destructive';
      default: return 'outline';
    }
  };

  // Get column config by field name
  const getColumnConfig = (field: string) => {
    return COLUMN_CONFIG.find(col => col.field === field);
  };

  // Handle sort - uses database field names
  const handleSort = (field: string) => {
    const columnConfig = getColumnConfig(field);
    // Only sort if column is sortable
    if (!columnConfig?.sortable) return;
    
    setCurrentPage(1);
    setSortConfig((current) => {
      if (!current || current.key !== field || current.direction === 'desc') {
        return { key: field, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key: field, direction: 'desc' };
      }
      return null;
    });
  };

  const handlePageSizeChange = (newSize: number) => {
    setCurrentPage(1);
    setPageSize(newSize);
  };

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Get sort icon for column
  const getSortIcon = (field: string) => {
    if (!sortConfig || sortConfig.key !== field) {
      return <ArrowUpDown className="h-4 w-4 text-muted-foreground" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="h-4 w-4" />
    ) : (
      <ArrowDown className="h-4 w-4" />
    );
  };

  const totalPages = Math.ceil((data?.total ?? 0) / pageSize);

  const handleExportCampaigns = async () => {
    setIsExporting(true);
    try {
      let query = supabase
        .from('campaigns')
        .select('*');

      if (search && search.trim() !== '') {
        query = query.ilike('title', `%${search}%`);
      }

      if (sortConfig) {
        query = query.order(sortConfig.key, {
          ascending: sortConfig.direction === 'asc',
        });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      const { data: exportData, error } = await query;

      if (error) throw error;
      if (!exportData || exportData.length === 0) {
        toast.error('No campaigns available to export');
        return;
      }

      const columns: string[] = Array.from(
        exportData.reduce((keys, row) => {
          Object.keys(row).forEach((key) => keys.add(key));
          return keys;
        }, new Set<string>())
      );

      const escapeCsvValue = (value: any) => {
        const stringValue = value == null
          ? ''
          : typeof value === 'object'
            ? JSON.stringify(value)
            : String(value);
        return `"${stringValue.replace(/"/g, '""')}"`;
      };

      const csvRows = [
        columns.join(','),
        ...exportData.map((row) => columns.map((column) => escapeCsvValue(row[column])).join(',')),
      ];

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `brand-campaigns-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${exportData.length} campaigns`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to export campaigns');
    } finally {
      setIsExporting(false);
    }
  };

  const handleSave = () => {
    if (!viewing) return;
    updateCampaignMutation.mutate(viewing);
  };

  const handleDownloadImage = (url: string) => {
    window.open(url, '_blank');
  };

  const HIDDEN_FIELDS = ['id', 'title', 'status', 'brand_id', 'user_id', 'created_at', 'updated_at', 'product_image_url', 'budget_allocation_percent', 'gmv_buckets', 'is_active', 'open_brief_allocation'];

  const formatFieldLabel = (key: string) => key.replace(/_/g, ' ');

  const formatFieldValue = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) return value.join(', ');
    return String(value);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl sm:text-2xl">Brand Campaigns ({data?.total ?? 0})</CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleExportCampaigns} disabled={isExporting || !data?.data?.length}>
                <Download className="mr-2 h-4 w-4" />
                {isExporting ? 'Exporting...' : 'Export'}
              </Button>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by title..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-8 w-[300px]"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                {COLUMN_CONFIG.map((column) => (
                  <TableHead key={column.field} className="whitespace-nowrap">
                    {column.sortable ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-ml-3 h-8 data-[state=open]:bg-accent"
                        onClick={() => handleSort(column.field)}
                      >
                        <span>{column.label}</span>
                        {getSortIcon(column.field)}
                      </Button>
                    ) : (
                      // No px-3 — it would offset this label 12px from the
                      // cell text below (the sortable Button cancels its own).
                      <span>{column.label}</span>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading...</TableCell>
                </TableRow>
              )}
              {!isLoading && (!data?.data || data.data.length === 0) && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No brand campaigns found</TableCell>
                </TableRow>
              )}
              {data?.data?.map((c: any) => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{c.title || '—'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">{c.brand_id ? c.brand_id.slice(0, 8) + '...' : '—'}</TableCell>
                  <TableCell><Badge variant={statusColor(c.status)}>{c.status || 'unknown'}</Badge></TableCell>
                  <TableCell className="whitespace-nowrap">{c.budget_allocation_percent != null ? `$${Number(c.budget_allocation_percent).toLocaleString()}` : '—'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setViewing({ ...c })}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {c.status === 'pending' && (
                        <>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" title="Activate">
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Activate this campaign?</AlertDialogTitle>
                                <AlertDialogDescription>This will make the campaign live and visible to creators.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => updateStatusMutation.mutate({ id: c.id, status: 'active' })}>Activate</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" title="Reject">
                                <XCircle className="h-4 w-4 text-destructive" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Reject this campaign?</AlertDialogTitle>
                                <AlertDialogDescription>The brand will be notified that their campaign was not approved.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => updateStatusMutation.mutate({ id: c.id, status: 'rejected' })} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Reject</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {data?.data && data.data.length > 0 && (
            <DataPagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={data?.total ?? 0}
              onPageChange={setCurrentPage}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </CardContent>
      </Card>

      <Sheet open={!!viewing} onOpenChange={(open) => { if (!open) setViewing(null); }}>
        <SheetContent className="overflow-y-auto w-full sm:max-w-lg">
          <SheetHeader><SheetTitle>Brand Campaign Details</SheetTitle></SheetHeader>
          {viewing && (
            <div className="space-y-4 mt-6">
              {/* Status */}
              <div>
                <Label>Status</Label>
                <div className="mt-1">
                  <Badge variant={statusColor(viewing.status)} className="text-sm">{viewing.status || 'unknown'}</Badge>
                </div>
              </div>

              {/* Title */}
              <div>
                <Label>Title</Label>
                <Input value={viewing.title || ''} onChange={e => setViewing({ ...viewing, title: e.target.value })} />
              </div>

              {/* IDs */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Brand ID</Label>
                  <Input value={viewing.brand_id || ''} readOnly className="font-mono text-xs bg-muted" />
                </div>
                <div>
                  <Label>User ID</Label>
                  <Input value={viewing.user_id || ''} readOnly className="font-mono text-xs bg-muted" />
                </div>
              </div>

              {/* Product Image */}
              {viewing.product_image_url && (
                <div>
                  <Label>Product Image</Label>
                  <div className="mt-1 space-y-2">
                    <img
                      src={viewing.product_image_url}
                      alt="Product"
                      className="w-full max-h-48 object-contain rounded-md border"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadImage(viewing.product_image_url)}
                    >
                      <Download className="mr-2 h-4 w-4" /> Download Image
                    </Button>
                  </div>
                </div>
              )}

              <Separator />

              {/* Budget Section */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm">Budget</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Total Brand Budget ($)</Label>
                    <Input
                      value={viewing.budget_allocation_percent ?? ''}
                      onChange={e => setViewing({ ...viewing, budget_allocation_percent: e.target.value })}
                      type="number"
                    />
                  </div>
                  <div>
                    <Label>Creator Reward Prize Pool</Label>
                    <Input
                      value={viewing.budget_allocation_percent != null ? `$${(Number(viewing.budget_allocation_percent) * 0.15).toFixed(2)}` : ''}
                      readOnly
                      className="bg-muted"
                    />
                    <p className="text-xs text-muted-foreground mt-1">15% of Total Brand Budget</p>
                  </div>
                </div>
                <div>
                  <Label>Open Brief Allocation (%)</Label>
                  <Input
                    value={viewing.open_brief_allocation ?? ''}
                    onChange={e => setViewing({ ...viewing, open_brief_allocation: e.target.value })}
                    type="number"
                  />
                </div>
              </div>

              <Separator />

              {/* Other fields */}
              {Object.entries(viewing)
                .filter(([key]) => !HIDDEN_FIELDS.includes(key))
                .map(([key, value]) => (
                  <div key={key}>
                    <Label className="capitalize">{formatFieldLabel(key)}</Label>
                    <Input
                      value={formatFieldValue(value)}
                      onChange={e => setViewing({ ...viewing, [key]: e.target.value })}
                    />
                  </div>
                ))}

              <Separator />

              {/* Briefs Section */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm">Video Briefs</h3>
                {(!briefs || briefs.length === 0) ? (
                  <p className="text-sm text-muted-foreground">No briefs submitted for this campaign.</p>
                ) : (
                  <div className="space-y-3">
                    {briefs.map((brief: any, idx: number) => {
                      const copyText = (text: string) => {
                        navigator.clipboard.writeText(text);
                        toast.success('Copied to clipboard');
                      };
                      return (
                      <Card key={brief.id}>
                        <CardContent className="p-3 space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Video className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium text-sm">{brief.brief_name || `Brief ${idx + 1}`}</span>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyText(brief.brief_name || '')}>
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {brief.brief_type || 'required'}
                            </Badge>
                          </div>

                          {brief.example_videos && brief.example_videos.length > 0 && (
                            <div className="space-y-1">
                              <div className="flex items-center gap-1">
                                <span className="text-xs font-medium">Example Videos</span>
                                <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copyText(brief.example_videos.join('\n'))}>
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                              {brief.example_videos.map((url: string, i: number) => (
                                <div key={i} className="flex items-center gap-1">
                                  <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline truncate flex-1">
                                    {url}
                                  </a>
                                  <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0" onClick={() => copyText(url)}>
                                    <Copy className="h-3 w-3" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}

                          {brief.description && (
                            <div>
                              <div className="flex items-center gap-1">
                                <span className="text-xs font-medium">Description</span>
                                <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copyText(brief.description)}>
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                              <p className="text-xs text-muted-foreground">{brief.description}</p>
                            </div>
                          )}

                          {brief.must_include && (
                            <div>
                              <div className="flex items-center gap-1">
                                <span className="text-xs font-medium">Must include</span>
                                <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copyText(brief.must_include)}>
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                              <p className="text-xs text-muted-foreground">{brief.must_include}</p>
                            </div>
                          )}

                          {brief.must_not_include && (
                            <div>
                              <div className="flex items-center gap-1">
                                <span className="text-xs font-medium">Must not include</span>
                                <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copyText(brief.must_not_include)}>
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                              <p className="text-xs text-muted-foreground">{brief.must_not_include}</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                      );
                    })}
                  </div>
                )}
              </div>

              <Separator />

              {/* Action buttons */}
              <div className="flex gap-2">
                <Button className="flex-1" onClick={handleSave} disabled={updateCampaignMutation.isPending}>
                  {updateCampaignMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
                {viewing.status !== 'active' && (
                  <Button
                    variant="default"
                    onClick={() => updateStatusMutation.mutate({ id: viewing.id, status: 'active' })}
                    disabled={updateStatusMutation.isPending}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="mr-2 h-4 w-4" /> Activate
                  </Button>
                )}
                {viewing.status !== 'rejected' && (
                  <Button
                    variant="destructive"
                    onClick={() => updateStatusMutation.mutate({ id: viewing.id, status: 'rejected' })}
                    disabled={updateStatusMutation.isPending}
                  >
                    <XCircle className="mr-2 h-4 w-4" /> Reject
                  </Button>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
